import React, { useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import './ScreenshotLightbox.css';

/** Horizontal travel, in px, past which a touch drag counts as a swipe. */
const SWIPE_THRESHOLD = 50;

function ChevronLeftIcon() {
  return (
    <svg
      width="18"
      height="18"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <polyline points="15 18 9 12 15 6" />
    </svg>
  );
}

function ChevronRightIcon() {
  return (
    <svg
      width="18"
      height="18"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <polyline points="9 18 15 12 9 6" />
    </svg>
  );
}

function CloseIcon() {
  return (
    <svg
      width="16"
      height="16"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      aria-hidden="true"
    >
      <line x1="18" y1="6" x2="6" y2="18" />
      <line x1="6" y1="6" x2="18" y2="18" />
    </svg>
  );
}

/**
 * Full-screen screenshot viewer, layered over the detail modal.
 *
 * Replaces `fslightbox-react`. Navigation wraps in both directions and the
 * index is owned by the caller, so the strip that opened the lightbox stays the
 * single source of truth for which screenshot is showing. The arrows, arrow
 * keys and a horizontal swipe over the image all drive the same `navigate`.
 *
 * Keys are handled on `window` in the capture phase and consumed there: the
 * lightbox sits inside a MUI `Dialog` that closes on Escape itself, and without
 * consuming the key both layers would dismiss at once.
 */
export default function ScreenshotLightbox({ screenshots, index, appName, onClose, onIndexChange }) {
  const count = screenshots.length;
  const src = screenshots[index];
  const touchStart = useRef(null);

  function navigate(delta) {
    onIndexChange((index + delta + count) % count);
  }

  function handleTouchStart(event) {
    const point = event.touches[0];
    if (!point) return;
    touchStart.current = { x: point.clientX, y: point.clientY };
  }

  function handleTouchEnd(event) {
    const start = touchStart.current;
    touchStart.current = null;
    const point = event.changedTouches[0];
    if (!start || !point) return;

    const dx = point.clientX - start.x;
    const dy = point.clientY - start.y;
    if (Math.abs(dx) < SWIPE_THRESHOLD || Math.abs(dx) <= Math.abs(dy)) return;

    navigate(dx < 0 ? 1 : -1);
  }

  useEffect(() => {
    if (!src) return undefined;

    function handleKeyDown(event) {
      if (event.key === 'Escape') {
        event.stopPropagation();
        onClose();
      } else if (event.key === 'ArrowLeft' || event.key === 'ArrowRight') {
        event.stopPropagation();
        event.preventDefault();
        const step = event.key === 'ArrowLeft' ? -1 : 1;
        onIndexChange((index + step + count) % count);
      }
    }

    window.addEventListener('keydown', handleKeyDown, true);
    return () => window.removeEventListener('keydown', handleKeyDown, true);
  }, [src, index, count, onClose, onIndexChange]);

  if (!src) return null;

  const step = (delta) => (event) => {
    event.stopPropagation();
    navigate(delta);
  };

  const position = `Screenshot ${index + 1} of ${count}`;

  return (
    <div
      className="itcLightbox"
      data-testid="itcLightboxScrim"
      onClick={onClose}
      role="presentation"
    >
      {count > 1 && (
        <button
          type="button"
          className="itcLightboxArrow itcLightboxArrowPrev"
          onClick={step(-1)}
          aria-label="Previous screenshot"
        >
          <ChevronLeftIcon />
        </button>
      )}

      <div
        className="itcLightboxStage"
        onClick={(event) => event.stopPropagation()}
        onTouchStart={handleTouchStart}
        onTouchEnd={handleTouchEnd}
        role="presentation"
      >
        <div className="itcLightboxFrame">
          <img src={src} alt={`${appName} screenshot ${index + 1} of ${count}`} />
        </div>
        <p className="itcLightboxCaption">{position}</p>
      </div>

      {count > 1 && (
        <button
          type="button"
          className="itcLightboxArrow itcLightboxArrowNext"
          onClick={step(1)}
          aria-label="Next screenshot"
        >
          <ChevronRightIcon />
        </button>
      )}

      <button
        type="button"
        className="itcLightboxClose"
        onClick={onClose}
        aria-label="Close screenshot viewer"
      >
        <CloseIcon />
      </button>
    </div>
  );
}

ScreenshotLightbox.propTypes = {
  screenshots: PropTypes.arrayOf(PropTypes.string).isRequired,
  index: PropTypes.number.isRequired,
  appName: PropTypes.string.isRequired,
  onClose: PropTypes.func.isRequired,
  onIndexChange: PropTypes.func.isRequired,
};
